import { useComputedColorScheme } from '@mantine/core';
import { useEffect, useRef } from 'react';

/**
 * The night sky the archive sits under.
 *
 * Three depth layers drift against the pointer rather than following it, so moving the
 * mouse reads as parallax through the field instead of a swarm chasing the cursor.
 * Near the pointer the brightest stars find each other and draw a constellation, which
 * fades as soon as you move on. A meteor crosses every so often.
 *
 * Deliberately quiet: nothing here should compete with a writeup for attention.
 */

interface Star {
  x: number;          // position in field space (0..1)
  y: number;
  r: number;          // radius in css px
  base: number;       // resting brightness
  phase: number;      // twinkle offset, so the field never pulses in unison
  speed: number;
  depth: number;      // parallax factor
  warm: boolean;
}

interface Meteor {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  ttl: number;
}

const LAYERS = [
  { count: 0.00014, r: [0.5, 0.9], depth: 6, base: [0.18, 0.36] },
  { count: 0.00009, r: [0.8, 1.35], depth: 15, base: [0.3, 0.55] },
  { count: 0.00004, r: [1.2, 1.9], depth: 28, base: [0.5, 0.85] },
];

const LINK_RADIUS = 150;   // how close to the pointer a star must be to join a constellation
const LINK_RADIUS_SQ = LINK_RADIUS * LINK_RADIUS;
const LINK_SPAN = 96;      // how close two stars must be to each other to be linked
const LINK_SPAN_SQ = LINK_SPAN * LINK_SPAN;
const MAX_LINKS = 14;

const rand = (min: number, max: number) => min + Math.random() * (max - min);

export const Starfield = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scheme = useComputedColorScheme('dark', { getInitialValueInEffect: true });

  useEffect(() => {
    // The field is a night sky; on the light theme there is nothing to show.
    if (scheme !== 'dark') return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let stars: Star[] = [];
    let meteors: Meteor[] = [];
    let frame = 0;
    let nextMeteor = 6000;

    // Pointer is tracked in two parts: the raw target, and a smoothed value that
    // actually drives the parallax, so the field glides instead of snapping.
    const pointer = { x: -9999, y: -9999, sx: -9999, sy: -9999, active: false };

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const area = width * height;
      stars = [];
      LAYERS.forEach((layer) => {
        const count = Math.round(area * layer.count);
        for (let i = 0; i < count; i += 1) {
          stars.push({
            x: Math.random(),
            y: Math.random(),
            r: rand(layer.r[0], layer.r[1]),
            base: rand(layer.base[0], layer.base[1]),
            phase: Math.random() * Math.PI * 2,
            speed: rand(0.0004, 0.0012),
            depth: layer.depth,
            warm: Math.random() < 0.12,
          });
        }
      });
    };

    const spawnMeteor = () => {
      const fromLeft = Math.random() < 0.5;
      const speed = rand(0.09, 0.16);
      const angle = rand(0.35, 0.6);
      meteors.push({
        x: fromLeft ? rand(-0.1, 0.4) * width : rand(0.6, 1.1) * width,
        y: rand(-0.05, 0.35) * height,
        vx: (fromLeft ? 1 : -1) * speed * Math.cos(angle),
        vy: speed * Math.sin(angle),
        life: 0,
        ttl: rand(900, 1500),
      });
    };

    const draw = (time: number) => {
      ctx.clearRect(0, 0, width, height);

      // Ease the pointer toward its target.
      if (pointer.active) {
        pointer.sx += (pointer.x - pointer.sx) * 0.06;
        pointer.sy += (pointer.y - pointer.sy) * 0.06;
      }

      const cx = width / 2;
      const cy = height / 2;
      const offX = pointer.active ? (pointer.sx - cx) / cx : 0;
      const offY = pointer.active ? (pointer.sy - cy) / cy : 0;

      const lit: Array<{ x: number; y: number; a: number }> = [];

      stars.forEach((star) => {
        // Layers shift against the pointer — the near field moves most.
        const px = star.x * width - offX * star.depth;
        const py = star.y * height - offY * star.depth;

        const twinkle = reduced ? 1 : 0.62 + 0.38 * Math.sin(time * star.speed + star.phase);
        let alpha = star.base * twinkle;
        let radius = star.r;

        if (pointer.active) {
          const dx = px - pointer.sx;
          const dy = py - pointer.sy;
          const d2 = dx * dx + dy * dy;
          if (d2 < LINK_RADIUS_SQ) {
            const near = 1 - Math.sqrt(d2) / LINK_RADIUS;
            alpha = Math.min(1, alpha + near * 0.55);
            radius += near * 0.5;
            if (star.base > 0.42 && lit.length < 40) lit.push({ x: px, y: py, a: near });
          }
        }

        ctx.fillStyle = star.warm
          ? `rgba(255, 236, 202, ${alpha})`
          : `rgba(255, 255, 255, ${alpha})`;
        if (radius < 1) {
          // A sub-pixel dot is indistinguishable from a rect, and far cheaper to fill.
          const d = radius * 2;
          ctx.fillRect(px, py, d, d);
        } else {
          ctx.beginPath();
          ctx.arc(px, py, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Constellation: join the bright stars the pointer has woken up.
      if (lit.length > 1) {
        let drawn = 0;
        ctx.lineWidth = 0.6;
        for (let i = 0; i < lit.length && drawn < MAX_LINKS; i += 1) {
          for (let j = i + 1; j < lit.length && drawn < MAX_LINKS; j += 1) {
            const dx = lit[i].x - lit[j].x;
            const dy = lit[i].y - lit[j].y;
            const d2 = dx * dx + dy * dy;
            if (d2 > LINK_SPAN_SQ) continue;
            const strength = (1 - Math.sqrt(d2) / LINK_SPAN) * Math.min(lit[i].a, lit[j].a);
            ctx.beginPath();
            ctx.moveTo(lit[i].x, lit[i].y);
            ctx.lineTo(lit[j].x, lit[j].y);
            ctx.strokeStyle = `rgba(200, 214, 240, ${strength * 0.42})`;
            ctx.stroke();
            drawn += 1;
          }
        }
      }

      if (!reduced) {
        nextMeteor -= 16;
        if (nextMeteor <= 0) {
          spawnMeteor();
          nextMeteor = rand(9000, 20000);
        }
        meteors = meteors.filter((m) => m.life < m.ttl);
        meteors.forEach((m) => {
          m.life += 16;
          m.x += m.vx * 16;
          m.y += m.vy * 16;
          const t = m.life / m.ttl;
          const fade = Math.sin(Math.PI * t);           // in and out, never a hard pop
          const tailX = m.x - m.vx * 260;
          const tailY = m.y - m.vy * 260;
          const gradient = ctx.createLinearGradient(m.x, m.y, tailX, tailY);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${0.5 * fade})`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.beginPath();
          ctx.lineWidth = 1.1;
          ctx.strokeStyle = gradient;
          ctx.moveTo(m.x, m.y);
          ctx.lineTo(tailX, tailY);
          ctx.stroke();
        });
      }

      frame = window.requestAnimationFrame(draw);
    };

    const onMove = (event: PointerEvent) => {
      if (!pointer.active) {
        pointer.sx = event.clientX;
        pointer.sy = event.clientY;
      }
      pointer.active = true;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
    };
    const onLeave = () => { pointer.active = false; };
    const onVisibility = () => {
      window.cancelAnimationFrame(frame);
      if (!document.hidden) frame = window.requestAnimationFrame(draw);
    };

    build();
    frame = window.requestAnimationFrame(draw);
    window.addEventListener('resize', build);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', build);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [scheme]);

  if (scheme !== 'dark') return null;
  return (
    <div className="luna-sky" aria-hidden="true">
      <canvas ref={canvasRef} />
      <span className="luna-sky__glow" />
    </div>
  );
};
